/*
 * fullstar-shop.js — the small amount of behaviour the shop pages need.
 *
 * Deliberately small. Everything that CAN be a link or a form is one:
 * variant chips are links to ?variant=, add-to-cart is a real form post. So
 * this file only handles the four things that genuinely cannot work without
 * script, and every one of them degrades to something usable if it never runs:
 *
 *   1. Gallery thumbnails      — without it, the main image is still correct,
 *                                the strip still scrolls, and only the fade cue
 *                                at its right edge is missing
 *   2. Product tabs            — without it, the first panel is shown
 *   3. Quantity stepper        — without it, the number input still types
 *   4. The sticky buy bar      — without it, it stays hidden, and the real
 *                                buy box above is untouched
 *   5. The basket drawer       — opening it from the header and after an add.
 *                                Without it the cart button is still a link and
 *                                every add is still a plain form post.
 *
 * It is delegated from the document, so sections re-rendered by the theme
 * editor keep working without re-binding.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------- 1. thumbnails */
  /* Hover shows the picture, as well as click. A shopper skimming a strip of
     eight should not have to click each one to see it.
     Two things this has to get right, neither of which the click-only version
     needed to care about:
       - Setting .src on the live <img> blanks it until the new file decodes, so
         running the mouse across the strip flickered white. The next picture is
         decoded off-screen first and swapped in only when it is ready.
       - Those loads finish out of order. Each swap checks it is still the one
         the pointer last asked for before it paints, so a slow early hover
         cannot overwrite a later one. */
  function fsShowFull(thumb) {
    var full = thumb.getAttribute('data-full');
    var main = document.getElementById('fs-pdp-main');
    if (!full || !main) return;
    if (main.getAttribute('data-full') === full) return;
    main.setAttribute('data-full', full);

    document.querySelectorAll('[data-fs-thumb]').forEach(function (t) {
      t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
    });

    var next = new Image();
    var apply = function () {
      if (main.getAttribute('data-full') !== full) return;
      main.removeAttribute('srcset');
      main.src = full;
    };
    next.onload = apply;
    next.onerror = apply;
    next.src = full;
    if (next.complete) apply();
  }

  document.addEventListener('click', function (e) {
    var thumb = e.target.closest('[data-fs-thumb]');
    if (thumb) fsShowFull(thumb);
  });

  document.addEventListener('mouseover', function (e) {
    var thumb = e.target.closest('[data-fs-thumb]');
    if (thumb) fsShowFull(thumb);
  });

  /* The thumbnail strip's "there is more" fade. Painted only while the strip
     actually overflows AND is not already at its end — a fade that never goes
     away is a promise of more pictures that do not exist, which is worse than
     no cue at all. */
  function fsPaintThumbFade(strip) {
    var wrap = strip.closest('[data-fs-thumbwrap]');
    if (!wrap) return;
    var slack = strip.scrollWidth - strip.clientWidth;
    var more = slack > 4 && strip.scrollLeft < slack - 4;
    wrap.classList.toggle('is-scrollable', more);
  }
  function fsPaintAllThumbFades() {
    document.querySelectorAll('[data-fs-thumbs]').forEach(fsPaintThumbFade);
  }
  document.addEventListener('scroll', function (e) {
    var strip = e.target && e.target.closest && e.target.closest('[data-fs-thumbs]');
    if (strip) fsPaintThumbFade(strip);
  }, true); /* capture: scroll does not bubble */
  window.addEventListener('resize', fsPaintAllThumbFades);
  window.addEventListener('load', fsPaintAllThumbFades);
  document.addEventListener('DOMContentLoaded', fsPaintAllThumbFades);
  fsPaintAllThumbFades();

  /* ----------------------------------------------- 1b. the basket drawer */
  /* The drawer was in every page and nothing could open it. Dawn's CartDrawer
     binds itself to #cart-icon-bubble, which is its own header's cart link;
     fs-header does not have one, and the unguarded lookup threw inside the
     CONSTRUCTOR, so the element never upgraded at all. cart-drawer.js is now
     guarded, and the binding lives here instead.

     Everything below degrades: with this file absent the cart button is still a
     link to /cart and every add button is still a form post that adds and lands
     on the basket. */
  function fsDrawer() {
    var d = document.querySelector('cart-drawer');
    return d && typeof d.open === 'function' ? d : null;
  }

  /* the header's own count, which Dawn's section swap does not know about */
  function fsPaintCount(count) {
    document.querySelectorAll('[data-fs-cart-count]').forEach(function (el) {
      el.textContent = count;
      var wrap = el.parentElement;
      if (wrap) wrap.hidden = !count;
    });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-fs-cart-open]');
    if (!trigger) return;
    var drawer = fsDrawer();
    if (!drawer) return;              /* no drawer on this page — follow the link */
    e.preventDefault();
    drawer.open(trigger);
  });

  /* Adding anything, from anywhere, opens the drawer instead of leaving the
     page: the product form, the sticky buy bar, and the upsell rows. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') < 0) return;

    var drawer = fsDrawer();
    if (!drawer || !window.routes || !window.routes.cart_add_url) return;

    var idField = form.querySelector('[name="id"]');
    if (!idField || !idField.value) return;
    e.preventDefault();

    var button = form.querySelector('[type="submit"]');
    if (button) button.disabled = true;

    var qtyField = form.querySelector('[name="quantity"]');
    var sections = [];
    try { sections = drawer.getSectionsToRender().map(function (x) { return x.id; }); } catch (err) {}

    fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: Number(idField.value), quantity: Number(qtyField && qtyField.value) || 1 }],
        sections: sections,
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (!state || state.status) throw new Error(state && state.description);
        if (state.sections) drawer.renderContents(state);   /* this also opens it */
        else drawer.open();
        return fetch(window.routes.cart_url + '.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) { fsPaintCount(cart.item_count); });
      })
      .catch(function () {
        /* Sold out, a stale variant, anything unexpected: hand it back to the
           ordinary post rather than swallowing the click. */
        form.submit();
      })
      .finally(function () { if (button) button.disabled = false; });
  });

  /* ---------------------------------------------------------------- 2. tabs */
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('[data-fs-tab]');
    if (!tab) return;
    var name = tab.getAttribute('data-fs-tab');
    var scope = tab.closest('.fs-pdp__tabsblock') || document;
    scope.querySelectorAll('[data-fs-tab]').forEach(function (t) {
      t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
    });
    scope.querySelectorAll('[data-fs-panel]').forEach(function (panel) {
      if (panel.getAttribute('data-fs-panel') === name) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  });

  /* ------------------------------------------------------------ 3. quantity */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-fs-qty-down], [data-fs-qty-up]');
    if (!btn) return;
    var group = btn.closest('[data-fs-qty]');
    if (!group) return;
    var input = group.querySelector('.fs-qty__value');
    if (!input) return;
    var min = parseInt(input.getAttribute('min') || '1', 10);
    var max = parseInt(input.getAttribute('max') || '99', 10);
    var now = parseInt(input.value || String(min), 10);
    if (isNaN(now)) now = min;
    var next = btn.hasAttribute('data-fs-qty-up') ? now + 1 : now - 1;
    next = Math.max(min, Math.min(max, next));
    input.value = String(next);
    group.querySelector('[data-fs-qty-down]').disabled = next <= min;
    group.querySelector('[data-fs-qty-up]').disabled = next >= max;
    // Let the theme's own cart code hear about it.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* -------------------------------------------------------- 4. sticky buy bar
     Shown once the real buy box has been scrolled past, hidden again when it
     comes back. Mirrors the prototype, which only shows the bar on the product
     route and only after the buy box leaves the screen. */
  var bar = document.querySelector('[data-fs-pdpbar]');
  var buybox = document.querySelector('[data-fs-buybox]');
  if (bar && buybox && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Only show it once the buy box is above the viewport, not when the
        // page first loads with the buy box below the fold.
        var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        if (scrolledPast) {
          bar.removeAttribute('hidden');
        } else {
          bar.setAttribute('hidden', '');
        }
      });
    }, { rootMargin: '0px', threshold: 0 });
    io.observe(buybox);
  }
})();

/*
 * ---------------------------------------------------------------- the finder
 *
 * Three questions, two shortcuts, one recommendation. The questions, the answer
 * matrix and the resolved outcomes all arrive as JSON from the section — none
 * of it is written here, so editing the quiz never means editing JavaScript.
 *
 * The two shortcuts are the prototype's and are load-bearing:
 *   answering "batch" to Q1        ends the quiz at one question
 *   answering "allinone" to Q2     ends it at two
 * quizLength() below is that rule, unchanged.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-fs-quiz]');
  var dataEl = document.querySelector('[data-fs-quiz-data]');
  if (!root || !dataEl) return;

  var DATA;
  try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!DATA || !DATA.questions) return;

  var picks = [];

  var el = {
    ask: root.querySelector('[data-fs-quiz-ask]'),
    done: root.querySelector('[data-fs-quiz-done]'),
    eyebrow: root.querySelector('[data-fs-quiz-eyebrow]'),
    question: root.querySelector('[data-fs-quiz-question]'),
    options: root.querySelector('[data-fs-quiz-options]'),
    bar: root.querySelector('[data-fs-quiz-bar]'),
    count: root.querySelector('[data-fs-quiz-count]'),
    back: root.querySelector('[data-fs-quiz-back]'),
    name: root.querySelector('[data-fs-quiz-name]'),
    why: root.querySelector('[data-fs-quiz-why]'),
    note: root.querySelector('[data-fs-quiz-note]'),
    see: root.querySelector('[data-fs-quiz-see]'),
    image: root.querySelector('[data-fs-quiz-image]'),
    price: root.querySelector('[data-fs-quiz-price]'),
    restart: root.querySelector('[data-fs-quiz-restart]'),
    addform: root.querySelector('[data-fs-quiz-addform]'),
    variant: root.querySelector('[data-fs-quiz-variant]')
  };

  function quizLength(p) {
    if (p[0] === 'batch') return 1;
    if (p[1] === 'allinone') return 2;
    return 3;
  }

  function quizOutcome(p) {
    var a = p[0], b = p[1], c = p[2];
    var m = DATA.matrix;
    if (a === 'batch') return m.batch;
    var branch = m[a] || m.family;
    if (b === 'allinone') return branch.allinone;
    var leaf = branch[b];
    if (!leaf) return 'orig-8';
    return leaf[c] || leaf.diceslice;
  }

  function render() {
    var total = quizLength(picks);
    if (picks.length >= total) return showResult();

    var q = DATA.questions[picks.length];
    el.ask.removeAttribute('hidden');
    el.done.setAttribute('hidden', '');
    el.eyebrow.textContent = q.eyebrow;
    el.question.textContent = q.q;
    el.count.textContent = (picks.length + 1) + ' ' + (DATA.labels.of || 'of') + ' ' + total;
    el.bar.style.width = ((picks.length / total) * 100).toFixed(0) + '%';
    el.back.hidden = picks.length === 0;

    el.options.textContent = '';
    q.opts.forEach(function (o) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'fs-quiz__opt';
      var l = document.createElement('span');
      l.className = 'fs-quiz__optlabel';
      l.textContent = o.label;
      var s = document.createElement('span');
      s.className = 'fs-quiz__optsub';
      s.textContent = o.sub;
      b.appendChild(l); b.appendChild(s);
      b.addEventListener('click', function () { picks.push(o.v); render(); });
      el.options.appendChild(b);
    });
  }

  function showResult() {
    var id = quizOutcome(picks);
    var out = DATA.outcomes[id];

    // An outcome nobody has pointed at a product yet must not render a dead
    // "See it" button, so prefer any outcome that IS wired up.
    if (!out || !out.url) {
      var keys = Object.keys(DATA.outcomes).filter(function (k) {
        return DATA.outcomes[k] && DATA.outcomes[k].url;
      });
      if (keys.length) out = DATA.outcomes[keys[0]];
    }

    // THIS USED TO `return` WHEN NOTHING WAS WIRED, AND THAT IS HOW THE QUIZ
    // BROKE. Every product reference in the template was stored in the wrong
    // format, so no outcome resolved, so the last answer silently did nothing —
    // and because the pick had already been pushed, "Back a question" then
    // re-rendered the SAME question and looked dead too. One data fault, two
    // symptoms, no error anywhere.
    //
    // Now the recommendation always appears. If there is genuinely nothing to
    // link to, the button is removed rather than the whole result being
    // withheld: a visible recommendation with no button is diagnosable, a
    // button that does nothing is not.
    if (!out) out = { name: '', why: '', url: '', price: '', image: '', spec: '', note: '' };

    el.ask.setAttribute('hidden', '');
    el.done.removeAttribute('hidden');
    // The eyebrow is shared chrome above both states. render() sets it to the
    // current question's own label and never touches it again, so without this
    // the result kept showing "Step 3 — the cuts" — the quiz's own step count,
    // for a quiz that had already ended.
    if (el.eyebrow) el.eyebrow.textContent = DATA.labels.resultEyebrow || 'Your match';
    el.name.textContent = out.name || '';
    el.why.textContent = out.why || '';
    if (out.url) {
      el.see.setAttribute('href', out.url);
      el.see.hidden = false;
    } else {
      el.see.removeAttribute('href');
      el.see.hidden = true;
    }
    el.price.textContent = out.price || '';
    if (out.image) {
      el.image.style.backgroundImage = 'url(' + out.image + ')';
      el.image.setAttribute('aria-label', out.name || '');
    }
    if (out.note) {
      el.note.textContent = out.note;
      el.note.removeAttribute('hidden');
    } else {
      el.note.setAttribute('hidden', '');
    }

    // Add to cart. Only ever shown for an outcome that resolved to a real
    // variant — a family collection has nothing to add, and offering the button
    // there would be a button that cannot work.
    if (el.addform && el.variant) {
      if (out.variant) {
        el.variant.value = out.variant;
        el.addform.hidden = false;
        var btn = el.addform.querySelector('button');
        if (btn) {
          var lab = btn.querySelector('span') || btn;
          lab.textContent = out.purchasable
            ? (DATA.labels.add || 'Add to cart')
            : (DATA.labels.soldOut || 'Sold out');
          btn.disabled = !out.purchasable;
        }
      } else {
        el.variant.value = '';
        el.addform.hidden = true;
      }
    }
  }

  // Trim to the question actually being shown, not blindly by one: if a result
  // is on screen the last pick belongs to the answer that produced it, and
  // popping one would land back on the same question.
  el.back.addEventListener('click', function () {
    var total = quizLength(picks);
    if (picks.length > total) picks.length = total;
    picks.pop();
    render();
  });
  el.restart.addEventListener('click', function () { picks = []; render(); });
  render();
})();
